import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

function monthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, (m || 1) - 1, 1);
  return new Intl.DateTimeFormat('ru-RU', { month: 'short' })
    .format(d)
    .replace('.', '');
}

function makeGradient(ctx, chartArea, rgbaTop) {
  const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  g.addColorStop(0, rgbaTop);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  return g;
}

const IncomeExpenseMonthsChart = ({ months = 6 }) => {
  const [points, setPoints] = useState([]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`/api/stats/months?count=${months}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Ошибка загрузки');

        if (!cancelled) setPoints(Array.isArray(data.points) ? data.points : []);
      } catch (e) {
        if (!cancelled) setPoints([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [months]);

  const padded = useMemo(() => {
    if (!points.length) return [];
    const first = points[0];
    const last = points[points.length - 1];
    return [first, ...points, last];
  }, [points]);

  const labels = useMemo(() => {
    const lastIndex = padded.length - 1;
    return padded.map((p, i) => {
      if (i === 0 || i === lastIndex) return '';
      return monthLabel(p.month);
    });
  }, [padded]);

  const income = useMemo(() => padded.map((p) => p.income || 0), [padded]);
  const expense = useMemo(() => padded.map((p) => p.expense || 0), [padded]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Доход',
          data: income,
          tension: 0.40,
          pointRadius: (ctx) => {
            const i = ctx.dataIndex;
            const len = ctx.dataset.data.length;
            return i === 0 || i === len - 1 ? 0 : 0;
          },
          pointHoverRadius: (ctx) => {
            const i = ctx.dataIndex;
            const len = ctx.dataset.data.length;
            return i === 0 || i === len - 1 ? 0 : 0;
          },
          borderWidth: 2,
          fill: true,
          backgroundColor: (context) => {
            const { chart } = context;
            const { ctx, chartArea } = chart;
            if (!chartArea) return '#7fcf9b';
            return makeGradient(ctx, chartArea, '#7fcf9b88');
          },
          borderColor: '#7fcf9b',
          order: 2,
        },
        {
          label: 'Расход',
          data: expense,
          tension: 0.4,
          pointRadius: (ctx) => {
            const i = ctx.dataIndex;
            const len = ctx.dataset.data.length;
            return i === 0 || i === len - 1 ? 0 : 0;
          },
          pointHoverRadius: (ctx) => {
            const i = ctx.dataIndex;
            const len = ctx.dataset.data.length;
            return i === 0 || i === len - 1 ? 0 : 0;
          },
          borderWidth: 2,
          fill: true,
          backgroundColor: (context) => {
            const { chart } = context;
            const { ctx, chartArea } = chart;
            if (!chartArea) return '#f08a8a';
            return makeGradient(ctx, chartArea, '#f08a8a88');
          },
          borderColor: '#f08a8a',
          order: 1,
        },
      ],
    }),
    [labels, income, expense]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: 'index',
          intersect: false,
          filter: (item) => {
            const i = item.dataIndex;
            const len = item.chart.data.labels.length;
            return i !== 0 && i !== len - 1;
          },
        },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        x: {
          grid: { display: false, drawTicks: false },
          border: { display: false },
          ticks: {
            color: '#cacaca',
            font: { size: 12, family: 'Mulish', weight: '400' },
          },
        },
        y: {
          grid: { display: false, drawTicks: false },
          border: { display: false },
          ticks: { display: false },
        },
      },
      elements: {
        line: { capBezierPoints: true },
      },
    }),
    []
  );

  return (
    <div style={{ height: 270, width: '100%', marginTop: 60 }}>
      <Line data={data} options={options} />
    </div>
  );
};

export default IncomeExpenseMonthsChart;
